import type { ListCount } from "@nexxt/contracts";
import { EmptyState, formatAge, Skeleton } from "@nexxt/ui";
import { type QueryKey, type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { Link, type LinkOptions } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { type ReactNode, useId } from "react";
import { errorText } from "../errors";
import { m } from "../paraglide/messages.js";
import { ErrorLine } from "./error-line";
import { LinkList } from "./link-list";

// The queues of the home "Now" (flussi-per-ruolo §1), drawn the same in every app (ADR-165): "your turn" first, then
// what waits on others, an empty queue never shown. Which queues, their words and their lists are the app's.

export interface WorkQueue {
  id: string;
  title: string;
  /** Who it waits on, for the "waiting on others" section. */
  on?: string;
  /** The list the queue opens, with the same filters it counts; none until the app has that list. */
  link?: LinkOptions;
  data: ListCount | undefined;
}

/**
 * What worklist answers (ADR-136): the count of every queue its domain answered; the others in `failed`; the count of
 * each union asked, in order, null where its domain did not answer (ADR-302).
 */
interface QueueCounts {
  counts: Partial<Record<string, ListCount>>;
  failed: string[];
  unions: (ListCount | null)[];
}

type Shown = Omit<WorkQueue, "data">;

/**
 * The queues the user's permissions open (`false`, one they do not), counted in one call: only the ones the two
 * sections show, and none when there is nothing to show. `counts` is the app's query of its gateway. `unionsOf` picks,
 * from the "your turn" queues, the unions to count as well (ADR-302), whose counts come back in `unions`, in order.
 */
export function useWorkQueues<S extends Shown, D extends QueueCounts, K extends QueryKey>(
  yourTurn: (S | false)[],
  waiting: (S | false)[],
  counts: (ids: S["id"][], unions: S["id"][][]) => UseQueryOptions<D, Error, D, K>,
  unionsOf: (yourTurn: S[]) => S["id"][][] = () => [],
) {
  const mine = yourTurn.filter((q): q is S => q !== false);
  const others = waiting.filter((q): q is S => q !== false);
  const asked = [...mine, ...others].map((q) => q.id);
  const unions = unionsOf(mine);
  const query = useQuery({ ...counts(asked, unions), enabled: asked.length > 0 });
  const counted = (queues: S[]) => queues.map((q) => ({ ...q, data: query.data?.counts[q.id] }));
  return {
    yourTurn: counted(mine),
    waiting: counted(others),
    unions: unions.map((ids, i) => ({ ids, data: query.data?.unions[i] })),
    loading: asked.length > 0 && query.isPending,
    error: query.error,
    failed: query.data?.failed ?? [],
    updatedAt: query.dataUpdatedAt,
  };
}

interface WorkQueuesProps {
  yourTurn: WorkQueue[];
  waiting: WorkQueue[];
  loading: boolean;
  /** The call that failed: never "nothing to do", which would not be true. */
  error?: unknown;
  /** The queues whose domain did not answer (ADR-136): the others show, and the page says some are missing. */
  failed?: string[];
  /** What closes a row, beside its link: the pin of the sidebar in Backstage. */
  trailing?: (queue: WorkQueue) => ReactNode;
}

export function WorkQueues({ yourTurn, waiting, loading, error, failed = [], trailing }: WorkQueuesProps) {
  if (loading) return <Skeleton className="h-24 w-full" />;
  if (error) return <ErrorLine>{errorText(error)}</ErrorLine>;
  const visible = (queues: WorkQueue[]) => queues.filter((q) => (q.data?.count ?? 0) > 0);
  const mine = visible(yourTurn);
  const others = visible(waiting);
  const missing = failed.length > 0 && <ErrorLine>{m.queues_some_missing()}</ErrorLine>;
  if (mine.length + others.length === 0)
    return missing || <EmptyState title={m.queues_empty_title()}>{m.queues_empty_hint()}</EmptyState>;
  return (
    <>
      {missing}
      <QueueSection title={m.queues_your_turn()} queues={mine} mine trailing={trailing} />
      <QueueSection title={m.queues_waiting_on_others()} queues={others} trailing={trailing} />
    </>
  );
}

function QueueSection({
  title,
  queues,
  mine = false,
  trailing,
}: {
  title: string;
  queues: WorkQueue[];
  mine?: boolean;
  trailing: WorkQueuesProps["trailing"];
}) {
  const rows = queues.map((q) => ({
    ...q,
    count: q.data?.count,
    detail: [q.on, q.data?.oldest && m.queue_oldest({ age: formatAge(q.data.oldest) })].filter(Boolean).join(" · "),
  }));
  return <CountSection title={title} rows={rows} mine={mine} trailing={trailing} />;
}

/** A row of a list of counts: the number, what it counts, and the list it opens. */
export interface CountRow {
  id: string;
  title: string;
  count: number | undefined;
  /** The muted second line: who it waits on, since when, or why there is no number. */
  detail?: string | undefined;
  /** The list it opens; none, and the row is only read. */
  link?: LinkOptions | undefined;
}

/**
 * A titled list of counts, each row opening its list: the queues of the home, and the Activity of an organization in
 * Backstage (ADR-322). `mine` marks the rows that are the user's turn.
 */
export function CountSection<R extends CountRow>({
  title,
  rows,
  mine = false,
  trailing,
}: {
  title: string;
  rows: R[];
  mine?: boolean;
  /** What closes a row, beside its link. */
  trailing?: ((row: R) => ReactNode) | undefined;
}) {
  const titleId = useId();
  if (rows.length === 0) return null;
  return (
    <section aria-labelledby={titleId} className="space-y-2">
      <h2 id={titleId} className="text-sm font-medium text-muted-foreground">
        {title}
      </h2>
      <LinkList>
        {rows.map((row) => (
          <li key={row.id} className={row.link ? "flex items-center hover:bg-accent" : "flex items-center"}>
            {row.link ? (
              <Link
                {...row.link}
                className={`${rowClass} focus-visible:outline-offset-[-2px] ${trailing ? "" : "pr-3 sm:pr-4"}`}
              >
                <CountRowContent row={row} mine={mine} />
                <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
              </Link>
            ) : (
              <div className={`${rowClass} pr-3 sm:pr-4`}>
                <CountRowContent row={row} mine={mine} />
              </div>
            )}
            {trailing?.(row)}
          </li>
        ))}
      </LinkList>
    </section>
  );
}

const rowClass = "flex min-w-0 flex-1 items-center gap-3 py-3 pl-3 sm:gap-4 sm:pl-4";

function CountRowContent({ row, mine }: { row: CountRow; mine: boolean }) {
  return (
    <>
      {mine && <span aria-hidden className="size-2 rounded-full bg-brand" />}
      <span className="w-12 text-lg font-semibold tabular-nums">{row.count}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{row.title}</span>
        {row.detail && <span className="block text-xs text-muted-foreground">{row.detail}</span>}
      </span>
    </>
  );
}
